import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRiskMatrixData } from '../../hooks/useDashboardQueries';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import riskService from '../../services/risk.service';
import { cn } from '../../lib/cn';

type MatrixType = 'inherent' | 'residual';

function zoneClass(score: number): string {
  if (score <= 3) return 'bg-zone-1';
  if (score <= 6) return 'bg-zone-2';
  if (score <= 10) return 'bg-zone-3';
  if (score <= 16) return 'bg-zone-4';
  return 'bg-zone-5';
}

export const LedgerRiskMatrix: React.FC = () => {
  const [type, setType] = useState<MatrixType>('residual');
  const { data, isPending } = useRiskMatrixData();
  const navigate = useNavigate();

  if (isPending) return <LedgerSkeleton lines={5} />;
  if (!data) return null;
  const grid = type === 'residual' ? data.matrix_residual : data.matrix_inherent;

  const exportPdf = async () => {
    const blob = await riskService.exportRiskMatrix(type);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risk-matrix-${type}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section aria-label="Risk matrix">
      <div className="flex items-baseline justify-between border-b border-ink pb-1">
        <span className="cap-label !text-ink">Risk matrix</span>
        <span className="flex gap-2">
          {(['inherent', 'residual'] as const).map((t) => (
            <button key={t} type="button" onClick={() => setType(t)}
              className={cn('cap-label', type === t && '!text-accent border-b border-accent')}>
              {t}
            </button>
          ))}
          <button type="button" onClick={exportPdf} className="cap-label hover:!text-ink">
            Export PDF
          </button>
        </span>
      </div>

      <div className="mt-3 flex gap-2">
        <div className="cap-label self-center !text-[9px]" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
          Likelihood ↑
        </div>
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-5 gap-0.5">
            {Array.from({ length: 5 }, (_, r) => 5 - r).map((likelihood) =>
              Array.from({ length: 5 }, (_, c) => c + 1).map((impact) => {
                const count = grid?.[`${likelihood},${impact}`] ?? 0;
                const label = `${count} risk${count === 1 ? '' : 's'} — likelihood ${likelihood}, impact ${impact}`;
                return (
                  <button key={`${likelihood}-${impact}`} type="button" title={label} aria-label={label}
                    onClick={() => navigate(`/risks?view=matrix&type=${type}&likelihood=${likelihood}&impact=${impact}`)}
                    className={cn('flex aspect-[5/4] items-center justify-center font-mono text-scale-1 text-ink',
                      zoneClass(likelihood * impact),
                      'hover:outline hover:outline-1 hover:outline-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent')}>
                    {count > 0 ? count : ''}
                  </button>
                );
              })
            )}
          </div>
          <div className="cap-label mt-1 text-center !text-[9px]">Impact →</div>
        </div>
      </div>
      <p className="mt-2 font-serif text-scale-1 italic text-ink-muted">
        Click any cell to open that slice of the register.
      </p>
    </section>
  );
};
